import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { LogOut, Users, Rocket, Crown, Calendar, FileText, ChevronRight, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, free: 0, premium: 0 });

  useEffect(() => {
    if (user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    loadUsers();
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
      
      const total = response.data.length;
      const premium = response.data.filter(u => u.subscription_plan === 'premium').length;
      const free = total - premium;
      setStats({ total, free, premium });
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" data-testid="admin-dashboard">
      {/* Header */}
      <nav className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <div className="w-9 h-9 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center">
                <Rocket className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                StartupBlackbox
              </span>
            </div>
            <span className="px-3 py-1 bg-red-500/10 text-red-400 text-sm rounded-full border border-red-500/30">
              Admin Panel
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-slate-300">{user.name}</span>
            <Button
              variant="ghost"
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="text-slate-400 hover:text-red-400"
              type="button"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-slate-800/40 border-slate-700 p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Total Users</p>
                <p className="text-2xl font-bold text-slate-100">{stats.total}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/40 border-slate-700 p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Users className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Free Users</p>
                <p className="text-2xl font-bold text-slate-100">{stats.free}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-slate-800/40 border-slate-700 p-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Crown className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Premium Users</p>
                <p className="text-2xl font-bold text-slate-100">{stats.premium}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Quick Actions */}
        <Card 
          className="bg-slate-800/40 border-slate-700 p-6 mb-4 cursor-pointer hover:border-cyan-500/50 transition-all"
          onClick={() => navigate('/admin/onboarding')}
          data-testid="onboarding-review-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <FileText className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-100">Review Onboarding Applications</h3>
                <p className="text-sm text-slate-400">View and approve startup funding applications</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        </Card>

        <Card 
          className="bg-slate-800/40 border-slate-700 p-6 mb-8 cursor-pointer hover:border-cyan-500/50 transition-all"
          onClick={() => navigate('/admin/benchmarks')}
          data-testid="benchmarks-card"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <h3 className="font-semibold text-slate-100">Set Metric Benchmarks</h3>
                <p className="text-sm text-slate-400">Configure target values and health score weights</p>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400" />
          </div>
        </Card>

        {/* Users Table */}
        <Card className="bg-slate-800/40 border-slate-700 p-6">
          <h2 className="text-2xl font-bold mb-6 text-slate-100">All Users</h2>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="users-table">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-4 text-slate-300 font-semibold">Name</th>
                  <th className="text-left py-3 px-4 text-slate-300 font-semibold">Email</th>
                  <th className="text-left py-3 px-4 text-slate-300 font-semibold">Role</th>
                  <th className="text-left py-3 px-4 text-slate-300 font-semibold">Plan</th>
                  <th className="text-left py-3 px-4 text-slate-300 font-semibold">Startups</th>
                  <th className="text-left py-3 px-4 text-slate-300 font-semibold">Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, index) => (
                  <tr
                    key={u.id}
                    data-testid={`user-row-${index}`}
                    className="border-b border-slate-700/50 hover:bg-slate-700/20"
                  >
                    <td className="py-3 px-4">
                      <div className="flex items-center space-x-3">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full" />
                        ) : (
                          <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center">
                            <span className="text-slate-400 text-sm">{u.name.charAt(0)}</span>
                          </div>
                        )}
                        <span className="text-slate-100">{u.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-slate-300">{u.email}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        u.role === 'admin'
                          ? 'bg-red-500/10 text-red-400 border border-red-500/30'
                          : 'bg-blue-500/10 text-blue-400 border border-blue-500/30'
                      }`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        u.subscription_plan === 'premium'
                          ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30'
                          : 'bg-slate-500/10 text-slate-400 border border-slate-500/30'
                      }`}>
                        {u.subscription_plan}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-slate-300">{u.startup_count}</td>
                    <td className="py-3 px-4 text-slate-400 text-sm">
                      {new Date(u.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default AdminDashboard;
